import * as React from "react";
import _ from "lodash";
import Dialog from "components/dialog";
import { theme } from "styles/theme";
import Button from "components/form/button";
import { CompanySelect } from "components/cart/company-select";
import { format } from "lib/globalize-util";
import { Form } from "formsy-react";
import SelectField from "components/form/select-field";
import MenuItem from "material-ui/MenuItem";
import fetch from "lib/fetch";
import { IOrder } from "records/order";
import { $, merge } from "glamor";
import type { IScope } from "reducers/scope";
import { TFDMCompanyRecord } from "records/company/company";
import { IUser } from "containers/management/management-types";
import { DATATEST } from "constants/integration";
import { IWarehouse } from "components/admin/admin-types";

import { IAddress as SchemaAddress } from "components/contacts/contacts-types";
import { SchemaRecord } from "records/schema";
import { MultiSelect } from "components/common";
const FIELD_WIDTH = 280;
type IActions = "start-order" | "advanced";

interface IProps {
    open: boolean;
    onRequestClose: () => void;
    onToggleCart: () => void;
    orderId: string;
    onSyncFromCart: (order: IOrder, meta: { syncToCart?: boolean }) => void;
    onSnackbarAdd: (message: string) => void;
    onFetchStartOrderCompanies: (params) => void;
    companyIds: string[];
    companies: TFDMCompanyRecord[];
    fetchingCompanies: boolean;
    hasMoreCompanies: boolean;
    autoRepAssign?: boolean;
    user: IUser;
    scope: IScope;
    schemas?: { [key: string]: SchemaRecord };
}

interface ICart {
    __uninitiated_order: boolean;
    assigned_rep?: { ref?: string; rep_name?: string; rep_email?: string };
    retailer?: { ref?: string; retailer_name?: string; buyer?: string; buyer_name?: string };
    currency_code?: string;
    msrp_currency_code?: string;
    discount?: number;
    additional_percentage?: number;
    selected_warehouse?: string;
    shipping_address?: SchemaAddress;
    order_type?: string;
    selected_shipping_locations?: any[];
}

interface ISettings {
    __default_discount: number;
    __default_surcharge: number;
}

interface IRep {
    __ref?: string;
    __name?: string;
    __email?: string;
}
interface IBuyer {
    __ref?: string;
    __name?: string;
    __reps?: IRep[];
}

interface IAddress {
    _id?: string;
    __country?: string;
    __display_name?: string;
    __default_shipping?: boolean;
    __shipping_code?: string;
    __line_1: string;
    __line_2?: string;
    __line_3?: string;
    __line_4?: string;
    __phone?: string;
    __city?: string;
    __state?: string;
    __zip?: string;
}

export function formatCartShippingAddress(shippingAddress: IAddress): SchemaAddress {
    return shippingAddress
        ? {
              ref: shippingAddress._id,
              code: shippingAddress.__shipping_code,
              display_name: shippingAddress.__display_name,
              country: shippingAddress.__country,
              city: shippingAddress.__city,
              state: shippingAddress.__state,
              phone: shippingAddress.__phone,
              zip: shippingAddress.__zip,
              line_1: shippingAddress.__line_1,
              line_2: shippingAddress.__line_2,
              line_3: shippingAddress.__line_3,
              line_4: shippingAddress.__line_4,
          }
        : null;
}

const formatForCart = (
    company: TFDMCompanyRecord,
    buyer: IBuyer,
    rep: IRep,
    currency: string,
    shippingAddress: IAddress,
    warehouse: any,
    action: IActions,
    useAdvancedPricing: boolean,
    orderType: string,
    noShipOrderType: string,
    selectedLocations: string[],
    selectedLocation: string,
    hasDiscountPreference: boolean,
    msrpCurrency: string | null
): ICart => {
    const cart: ICart = { __uninitiated_order: false };
    if (action === "start-order") {
        if (rep) {
            cart.assigned_rep = {
                ref: rep.__ref,
                rep_name: rep.__name,
                rep_email: rep.__email,
            };
        }

        if (company) {
            cart.retailer = {
                ref: company._id,
                retailer_name: company.__name,
            };

            if (buyer && buyer.__ref) {
                cart.retailer.buyer = buyer.__ref;
                cart.retailer.buyer_name = buyer.__name;
            }

            if (hasDiscountPreference && company.__settings.__default_discount) {
                cart.discount = company.__settings.__default_discount;
            }

            if (company.__settings.__default_surcharge) {
                cart.additional_percentage = company.__settings.__default_surcharge;
            }
        }
        if (useAdvancedPricing && shippingAddress) {
            cart.shipping_address = formatCartShippingAddress(shippingAddress);
        }
        if (currency) {
            cart.currency_code = currency;
        }
        if (warehouse) {
            cart.selected_warehouse = warehouse._id;
        }
        if (orderType) {
            cart.order_type = orderType;
        }
        if (orderType && noShipOrderType && orderType !== noShipOrderType) {
            if (company?.__settings.__allow_bulk_ordering && !_.isEmpty(selectedLocations)) {
                cart.selected_shipping_locations = _.map(selectedLocations, (location) => ({ location }));
            }
            if (company && !company.__settings.__allow_bulk_ordering && selectedLocation) {
                cart.shipping_address = formatCartShippingAddress(getLocationById(company, selectedLocation));
            }
        }
        if (msrpCurrency) {
            cart.msrp_currency_code = msrpCurrency;
        }
    }
    return cart;
};

const getDefaultCompany = ({ user, companies, companyIds }) => {
    // only try to default the company if user has 1 company choice
    if (user.account_type !== "buyer" || companyIds.length > 1) {
        return null;
    } else {
        return companies[companyIds[0]];
    }
};

const getDefaultBuyer = ({ user, defaultCompany }) => {
    if (!defaultCompany || user.account_type !== "buyer") {
        return null;
    } else {
        return defaultCompany.__buyers.find((buyerConnection) => buyerConnection.__ref === user._id);
    }
};

const getDefaultRep = ({ defaultBuyer, autoRepAssign = true }) => {
    if (!defaultBuyer || (!autoRepAssign && _.size(defaultBuyer.__reps) !== 1)) {
        return null;
    } else {
        return defaultBuyer.__reps[0];
    }
};

const getDefaultShippingAddress = ({ defaultCompany }) => {
    if (!defaultCompany) {
        return null;
    }

    const shippingAddresses = _.isArray(defaultCompany.__addresses)
        ? defaultCompany.__addresses
        : defaultCompany.__addresses.toArray();

    return shippingAddresses.filter((address) => address.__default_shipping)[0];
};

const getShippingLocations = (company: TFDMCompanyRecord): IAddress[] =>
    _.reject(company.__addresses.toArray(), { type: "billing" });

const getLocations = (company: TFDMCompanyRecord) =>
    _.map(getShippingLocations(company), (a) => ({ value: a._id, label: a.__display_name || a.__line_1 }));

const getLocationById = (company: TFDMCompanyRecord, id: string) => _.find(getShippingLocations(company), { _id: id });

const getDefaultCurrency = ({ defaultCompany, scope }) => {
    if (!defaultCompany) {
        return null;
    } else {
        const currency = scope.currencyCode;
        return defaultCompany.__currency_codes.includes(currency) ? currency : defaultCompany?.__currency_codes?.get(0);
    }
};

const locationStyles: { [key: string]: React.CSSProperties } = {
    select: {
        width: "280px",
    },
    items: {
        padding: "12px 16px 0px 50px",
        whiteSpace: "normal",
        lineHeight: "inherit",
        minHeight: "inherit",
        fontSize: "14px",
    },
    singleSelect: {
        whiteSpace: "normal",
        lineHeight: "inherit",
        margin: "6px 0px 0 6px",
        fontSize: "14px",
    },
};

export const StartOrder: React.FunctionComponent<IProps> = ({
    open,
    onRequestClose,
    onToggleCart,
    orderId,
    onSyncFromCart,
    onSnackbarAdd,
    onFetchStartOrderCompanies,
    fetchingCompanies,
    companies,
    companyIds,
    hasMoreCompanies,
    autoRepAssign = true,
    user,
    scope,
    schemas,
}) => {
    const useAdvancedPricing = scope.brand.isAdvancedPricingEnabled(scope);
    const hasDiscountPreference = scope.brand.hasPreference("discount_allowed");

    const styles = React.useMemo(() => {
        return {
            base: merge(
                $(" .subTitle", {
                    fontSize: "16px",
                    color: "rgba(45, 45, 45, 0.6)",
                    borderBottom: `1px solid ${theme.palette.borderColor}`,
                    paddingBottom: "10px",
                }),
                $(" .row", {
                    display: "flex",
                    margin: "0px",
                }),
                $(" .rightColumn", {
                    flex: "50%",
                    paddingRight: "12px",
                }),
                $(" .leftColumn", {
                    flex: "50%",
                    paddingLeft: "12px",
                }),
                $(" .advancedButton", {
                    marginRight: "20px !important",
                }),
                $(" .popover", {
                    width: "280px",
                    padding: "10px 0",
                }),
                $(" .advancedLink", {
                    float: "left",
                    marginTop: "10px",
                    color: theme.palette.primary1Color,
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontSize: "14px",
                }),
                $(" .rowContainer", {
                    width: FIELD_WIDTH,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "20px",
                })
            ),
            contentStyle: {
                width: "min-content",
            },
            bottomFields: {
                paddingTop: "15px",
            },
            requiredFieldLabel: {
                color: theme.palette.primary1Color,
            },
        };
    }, []);

    const [selectedCompany, setSelectedCompany] = React.useState<TFDMCompanyRecord | undefined>();
    const [selectedWarehouse, setSelectedWarehouse] = React.useState<IWarehouse>();
    const [selectedBuyer, setSelectedBuyer] = React.useState<IBuyer>();
    const [selectedRep, setSelectedRep] = React.useState<IRep>();
    const [selectedCurrency, setSelectedCurrency] = React.useState<string>();
    const [selectedMSRPCurrency, setSelectedMSRPCurrency] = React.useState<string>();
    const [selectedShippingAddress, setSelectedShippingAddress] = React.useState<IAddress>();

    const [companyErrorText, setCompanyErrorText] = React.useState<string>("");
    const [orderTypeError, setOrderTypeError] = React.useState<string>("");
    const [orderLocationsError, setOrderLocationsError] = React.useState<string>("");
    const [companyEdited, setCompanyEdited] = React.useState<boolean>(false);
    const [orderType, setOrderType] = React.useState<string>("");
    const [selectedLocations, setSelectedLocations] = React.useState<string[]>([]);
    const [selectedLocation, setSelectedLocation] = React.useState<string>(null);

    const displayWarehouse = React.useMemo<boolean>(
        () => scope.brand.hasPreference("set_warehouse_for_linesheets"),
        [scope.brand]
    );
    const orderedWarehouses = React.useMemo<IWarehouse[]>(
        () => _.orderBy(scope.brand.settings.warehouses, ["created_on"], ["desc"]),
        [scope.brand.settings.warehouses]
    );
    const warehouses = React.useMemo(() => {
        if (user.hasPermission("view_all_warehouses")) {
            return orderedWarehouses;
        }
        return _.intersectionWith(
            orderedWarehouses,
            selectedCompany?.__settings?.__warehouses,
            (warehouse, warehouseId) => warehouse._id === warehouseId
        );
    }, [orderedWarehouses, selectedCompany?.__settings?.__warehouses, user]);

    const orderTypeSchemaField = React.useMemo(
        () => _.find(schemas, { type: "order", brand: scope.brand._id })?.fields.get("order_type"),
        [schemas, scope.brand._id]
    );

    const orderTypes = React.useMemo(() => {
        const isBuyer = user.account_type === "buyer";
        const allowedValues: { value: string }[] = orderTypeSchemaField?.details?.allowed_values;

        if ((orderTypeSchemaField?.display && !isBuyer) || (orderTypeSchemaField?.display_to_buyers && isBuyer)) {
            return _.isEmpty(allowedValues) ? null : _.map(allowedValues, "value");
        }
        return null;
    }, [orderTypeSchemaField, user.account_type]);

    const noShipOrderType = React.useMemo(() => {
        const option =
            scope.brand.preferences.get("new_order_type_flow") && scope.brand.preferences.get("no_ship_to_orders");
        return _.find(orderTypes, (type) => type === option);
    }, [scope.brand.preferences, orderTypes]);

    const newOrderTypeFlowRequired = React.useMemo(
        () => scope.brand.preferences.get("new_order_type_flow") && orderTypeSchemaField?.required,
        [orderTypeSchemaField, scope.brand.preferences]
    );

    const useSeparateMSRPCurrency = React.useMemo(
        () => scope.brand?.hasPreference?.("separate_msrp_currency"),
        [scope.brand]
    );

    const orderTypeLabel = React.useMemo(
        () => `${format("Order Type")}${newOrderTypeFlowRequired ? "*" : ""}`,
        [newOrderTypeFlowRequired]
    );

    const showLocations = React.useMemo(
        () => selectedCompany && orderType && noShipOrderType && orderType !== noShipOrderType,
        [selectedCompany, orderType, noShipOrderType]
    );

    const shippingLocationsRequired = React.useMemo(
        () => showLocations && selectedCompany?.__settings?.__allow_bulk_ordering,
        [selectedCompany?.__settings?.__allow_bulk_ordering, showLocations]
    );

    const shippingLocationsLabel = React.useMemo(
        () => `${format("Location")}${shippingLocationsRequired ? "*" : ""}`,
        [shippingLocationsRequired]
    );

    React.useEffect(() => {
        setOrderLocationsError("");
    }, [showLocations]);

    React.useEffect(() => {
        if (displayWarehouse) {
            setSelectedWarehouse(_.head(warehouses));
        }
    }, [displayWarehouse, warehouses]);

    React.useEffect(() => {
        if (open) {
            const defaultCompany = getDefaultCompany({ user, companies, companyIds });
            const defaultBuyer = getDefaultBuyer({ user, defaultCompany });
            const defaultRep = getDefaultRep({ defaultBuyer, autoRepAssign });
            const defaultCurrencyCode = getDefaultCurrency({ defaultCompany, scope });
            const defaultShippingAddress = getDefaultShippingAddress({ defaultCompany });

            setSelectedCompany(defaultCompany);
            setSelectedBuyer(defaultBuyer);
            setSelectedRep(defaultRep);
            setSelectedCurrency(defaultCurrencyCode);
            setSelectedMSRPCurrency(defaultCompany?.__msrp_currency_codes?.get(0) || defaultCurrencyCode);
            setSelectedShippingAddress(defaultShippingAddress);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRepAssign, open]);

    React.useEffect(() => {
        let firstBuyer;
        let firstBuyerRep;
        let firstCurrency;
        if (selectedCompany) {
            firstBuyer = selectedCompany ? selectedCompany.__buyers.get(0) : null;
            firstBuyerRep = getDefaultRep({ defaultBuyer: firstBuyer, autoRepAssign });
            firstCurrency = getDefaultCurrency({ defaultCompany: selectedCompany, scope });
            const defaultShippingAddress = getDefaultShippingAddress({ defaultCompany: selectedCompany });
            setSelectedShippingAddress(defaultShippingAddress);
            setSelectedLocation(defaultShippingAddress?._id);
        }

        if (user.account_type === "buyer") {
            firstBuyer = selectedCompany ? selectedCompany.__buyers.find((buyer) => buyer.__ref === user._id) : null;
            firstBuyerRep = getDefaultRep({ defaultBuyer: firstBuyer, autoRepAssign });
        }
        setSelectedBuyer(firstBuyer);
        setSelectedRep(firstBuyerRep);
        setSelectedCurrency(firstCurrency);
        setSelectedMSRPCurrency(selectedCompany?.__msrp_currency_codes?.get(0) || firstCurrency);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRepAssign, selectedCompany]);

    const handleErrorTextChanged = React.useCallback((errorText: string) => {
        setCompanyErrorText(errorText);
        // indicate CompanySelect input value was updated
        setCompanyEdited(true);
    }, []);

    const handleSelectBuyer = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            const dataIndex = (e.currentTarget as HTMLDivElement).getAttribute("data-index");
            const buyer = selectedCompany.__buyers.get(parseInt(dataIndex));
            setSelectedBuyer(buyer);
            const firstBuyerRep = getDefaultRep({ defaultBuyer: buyer, autoRepAssign });
            setSelectedRep(firstBuyerRep);
        },
        [autoRepAssign, selectedCompany]
    );

    const handleSelectRep = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            const dataIndex = (e.currentTarget as HTMLDivElement).getAttribute("data-index");
            const rep = selectedBuyer.__reps[dataIndex];
            setSelectedRep(rep);
        },
        [selectedBuyer]
    );

    const handleSelectWarehouse = React.useCallback(
        (warehouse: IWarehouse) => () => setSelectedWarehouse(warehouse),
        []
    );

    const handleSelectCurrency = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            const currency = (e.currentTarget as HTMLDivElement).getAttribute("data-code");
            setSelectedCurrency(currency);
            if (useSeparateMSRPCurrency && !selectedCompany?.__msrp_currency_codes?.size) {
                setSelectedMSRPCurrency(currency);
            }
        },
        [selectedCompany?.__msrp_currency_codes, useSeparateMSRPCurrency]
    );

    const handleSelectMSRPCurrency = React.useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setSelectedMSRPCurrency(e.currentTarget.getAttribute("data-code"));
    }, []);

    const handleSelectOrderType = React.useCallback(
        (orderType: string) => () => {
            setOrderTypeError("");
            setOrderType(orderType);
        },
        []
    );

    const handleSelectedLocationsChange = React.useCallback((options: string[]) => {
        setOrderLocationsError("");
        setSelectedLocations(options);
    }, []);

    const handleSelectLocation = React.useCallback((id: string) => () => setSelectedLocation(id), []);

    const handleRequestClose = React.useCallback(() => {
        setCompanyErrorText(null);
        setSelectedBuyer(null);
        setSelectedRep(null);
        setSelectedCurrency("");
        setSelectedMSRPCurrency("");
        setSelectedShippingAddress(null);
        onRequestClose();
    }, [onRequestClose]);

    const validateCompany = React.useCallback(() => {
        if (companyEdited && !selectedCompany) {
            setCompanyErrorText(format("Invalid company name"));
            return false;
        }
        return true;
    }, [companyEdited, selectedCompany]);

    const validateOrderType = React.useCallback(() => {
        if (
            newOrderTypeFlowRequired &&
            !_.isEmpty(scope.brand.getPreference("no_ship_to_orders")) &&
            _.isEmpty(orderType)
        ) {
            setOrderTypeError(format("Order type is required"));
            return false;
        }
        return true;
    }, [newOrderTypeFlowRequired, orderType, scope.brand]);

    const validateLocations = React.useCallback(() => {
        if (shippingLocationsRequired && _.isEmpty(selectedLocations)) {
            setOrderLocationsError(format("Please select at least one location"));
            return false;
        }
        return true;
    }, [selectedLocations, shippingLocationsRequired]);

    const handleUpdateCart = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            const action = (e.currentTarget as HTMLDivElement).getAttribute("data-action") as IActions;
            if (
                action === "start-order" &&
                !_.every([validateCompany, validateOrderType, validateLocations], (validate) => validate())
            ) {
                return;
            } else {
                const cartUpdate = formatForCart(
                    selectedCompany,
                    selectedBuyer,
                    selectedRep,
                    selectedCurrency,
                    selectedShippingAddress,
                    displayWarehouse ? selectedWarehouse : null,
                    action,
                    useAdvancedPricing,
                    orderType,
                    noShipOrderType,
                    selectedLocations,
                    selectedLocation,
                    hasDiscountPreference,
                    useSeparateMSRPCurrency ? selectedMSRPCurrency : null
                );
                fetch(`/working/update/${orderId}`, {
                    method: "POST",
                    body: JSON.stringify(cartUpdate),
                })
                    .then((response) => {
                        if (response.ok) {
                            return response.json();
                        } else {
                            throw new Error(format("Problem starting order. Please refresh and try again."));
                        }
                    })
                    .then((data) => {
                        onSyncFromCart(
                            { ...data, __pricing_template: selectedCompany?.__settings?.__pricing_template },
                            { syncToCart: true }
                        );
                        handleRequestClose();
                        if (action === "advanced") {
                            onToggleCart();
                        }
                    })
                    .catch((error: Error) => {
                        onSnackbarAdd(error.message);
                    });
            }
        },
        [
            validateCompany,
            validateOrderType,
            validateLocations,
            selectedCompany,
            selectedBuyer,
            selectedRep,
            selectedCurrency,
            selectedShippingAddress,
            displayWarehouse,
            selectedWarehouse,
            useAdvancedPricing,
            hasDiscountPreference,
            orderType,
            noShipOrderType,
            selectedLocations,
            selectedLocation,
            orderId,
            onSyncFromCart,
            handleRequestClose,
            onToggleCart,
            onSnackbarAdd,
            selectedMSRPCurrency,
            useSeparateMSRPCurrency,
        ]
    );

    return (
        <Dialog
            title={
                <div>
                    <div>{format("Start Order")}</div>
                    <div className="subTitle">{format("Please specify the recipient of this order")}</div>
                </div>
            }
            modal={false}
            repositionOnUpdate={false}
            autoScrollBodyContent={false}
            open={open}
            onRequestClose={handleRequestClose}
            contentStyle={styles.contentStyle}
            contentClassName={`${styles.base}`}
            actions={[
                <div key="advanced" data-action="advanced" className="advancedLink" onClick={handleUpdateCart}>
                    {format("Go to Order Details")}
                </div>,
                <Button
                    label={format("Start Order")}
                    onClick={handleUpdateCart}
                    key="start-order"
                    data-action="start-order"
                    primary={true}
                />,
            ]}
        >
            <Form>
                <div className="row">
                    <div className="rightColumn">
                        <CompanySelect
                            open={open}
                            selectedCompany={selectedCompany}
                            onSelectedCompanyChange={setSelectedCompany}
                            errorText={companyErrorText}
                            onErrorTextChange={handleErrorTextChanged}
                            onFetchStartOrderCompanies={onFetchStartOrderCompanies}
                            fetchingCompanies={fetchingCompanies}
                            companies={companies}
                            companyIds={companyIds}
                            hasMoreCompanies={hasMoreCompanies}
                            user={user}
                            scope={scope}
                        />
                        <SelectField
                            label={format("Rep")}
                            name="rep"
                            width={`${FIELD_WIDTH}px`}
                            style={styles.bottomFields}
                            value={selectedRep ? selectedRep.__ref : ""}
                            data-test={DATATEST.SITE_HEADER.START_ORDER_MODAL.REP_SELECTOR}
                        >
                            {selectedBuyer &&
                                selectedBuyer.__reps.map((rep, index) => (
                                    <MenuItem
                                        key={rep.__ref}
                                        value={rep.__ref}
                                        primaryText={rep.__name}
                                        onClick={handleSelectRep}
                                        data-index={index}
                                        data-test={DATATEST.SITE_HEADER.START_ORDER_MODAL.REP_SELECT_OPTION}
                                    />
                                ))}
                        </SelectField>
                        {orderTypes && noShipOrderType && (
                            <SelectField
                                label={orderTypeLabel}
                                name="order_type"
                                width={`${FIELD_WIDTH}px`}
                                style={styles.bottomFields}
                                customLabelStyle={{ ...(newOrderTypeFlowRequired && styles.requiredFieldLabel) }}
                                errorText={orderTypeError}
                                value={orderType}
                                data-test={DATATEST.SITE_HEADER.START_ORDER_MODAL.ORDER_TYPE_SELECT_DROPDOWN}
                            >
                                <MenuItem
                                    key="<none>"
                                    primaryText={format("None")}
                                    value={""}
                                    onClick={handleSelectOrderType("")}
                                    data-test={DATATEST.SITE_HEADER.START_ORDER_MODAL.ORDER_TYPE_SELECT_OPTION}
                                />
                                {orderTypes.map((orderType) => (
                                    <MenuItem
                                        key={orderType}
                                        primaryText={orderType}
                                        value={orderType}
                                        onClick={handleSelectOrderType(orderType)}
                                        data-test={DATATEST.SITE_HEADER.START_ORDER_MODAL.ORDER_TYPE_SELECT_OPTION}
                                    />
                                ))}
                            </SelectField>
                        )}
                        {displayWarehouse && (
                            <SelectField
                                label={format("Warehouse")}
                                name="warehouse"
                                width={`${FIELD_WIDTH}px`}
                                style={styles.bottomFields}
                                value={selectedWarehouse?._id || ""}
                                data-test={DATATEST.SITE_HEADER.START_ORDER_MODAL.WAREHOUSE_SELECT_DROPDOWN}
                            >
                                {_.map(warehouses, (warehouse, index) => (
                                    <MenuItem
                                        key={warehouse._id}
                                        value={warehouse._id}
                                        primaryText={warehouse.name}
                                        onClick={handleSelectWarehouse(warehouse)}
                                        data-index={index}
                                        data-test={DATATEST.SITE_HEADER.START_ORDER_MODAL.WAREHOUSE_SELECT_OPTION}
                                    />
                                ))}
                            </SelectField>
                        )}
                    </div>
                    <div className="leftColumn">
                        <SelectField
                            label={format("Buyer")}
                            name="buyer"
                            width={`${FIELD_WIDTH}px`}
                            value={selectedBuyer ? selectedBuyer.__ref : ""}
                        >
                            {selectedCompany &&
                                selectedCompany.__buyers.map((buyer, index) => (
                                    <MenuItem
                                        key={buyer.__ref}
                                        value={buyer.__ref}
                                        primaryText={buyer.__name}
                                        onClick={handleSelectBuyer}
                                        data-index={index}
                                        data-test={DATATEST.SITE_HEADER.START_ORDER_MODAL.BUYER_SELECT_OPTION}
                                    />
                                ))}
                        </SelectField>
                        {!useSeparateMSRPCurrency && (
                            <SelectField
                                label={format("Currency")}
                                name="currencies"
                                width={`${FIELD_WIDTH}px`}
                                style={styles.bottomFields}
                                value={selectedCurrency || ""}
                            >
                                {selectedCompany &&
                                    selectedCompany.__currency_codes.map((code) => (
                                        <MenuItem
                                            key={code}
                                            value={code}
                                            primaryText={code}
                                            onClick={handleSelectCurrency}
                                            data-code={code}
                                            data-test={DATATEST.SITE_HEADER.START_ORDER_MODAL.CURRENCY_SELECT_OPTION}
                                        />
                                    ))}
                            </SelectField>
                        )}
                        {useSeparateMSRPCurrency && (
                            <div className="rowContainer">
                                <SelectField
                                    label={format("Wholesale")}
                                    name="currencies"
                                    width="100%"
                                    style={styles.bottomFields}
                                    value={selectedCurrency || ""}
                                >
                                    {selectedCompany &&
                                        selectedCompany.__currency_codes.map((code) => (
                                            <MenuItem
                                                key={code}
                                                value={code}
                                                primaryText={code}
                                                onClick={handleSelectCurrency}
                                                data-code={code}
                                                data-test={
                                                    DATATEST.SITE_HEADER.START_ORDER_MODAL.CURRENCY_SELECT_OPTION
                                                }
                                            />
                                        ))}
                                </SelectField>
                                <SelectField
                                    label={format("MSRP")}
                                    name="msrp_currencies"
                                    width="100%"
                                    style={styles.bottomFields}
                                    value={selectedMSRPCurrency || ""}
                                    disabled={!!selectedCompany && !selectedCompany?.__msrp_currency_codes?.size}
                                >
                                    {(selectedCompany && !selectedCompany?.__msrp_currency_codes?.size
                                        ? [selectedMSRPCurrency]
                                        : selectedCompany?.__msrp_currency_codes
                                    )?.map((code) => (
                                        <MenuItem
                                            key={code}
                                            value={code}
                                            primaryText={code}
                                            onClick={handleSelectMSRPCurrency}
                                            data-code={code}
                                            data-test={
                                                DATATEST.SITE_HEADER.START_ORDER_MODAL.CURRENCY_SELECT_MSRP_OPTION
                                            }
                                        />
                                    ))}
                                </SelectField>
                            </div>
                        )}
                        {selectedCompany &&
                            orderType &&
                            noShipOrderType &&
                            orderType === noShipOrderType &&
                            selectedCompany.__settings.__allow_bulk_ordering && (
                                <SelectField
                                    disabled={true}
                                    label={format("Shipping address")}
                                    name="location"
                                    width={`${FIELD_WIDTH}px`}
                                    style={styles.bottomFields}
                                    value={0}
                                    dataTest={DATATEST.SITE_HEADER.START_ORDER_MODAL.LOCATIONS_SELECTOR}
                                >
                                    <MenuItem
                                        value={0}
                                        primaryText={format("All Locations Combined")}
                                        data-test={DATATEST.SITE_HEADER.START_ORDER_MODAL.LOCATIONS_SELECTOR_OPTION}
                                    />
                                </SelectField>
                            )}
                        {shippingLocationsRequired && (
                            <MultiSelect
                                label={shippingLocationsLabel}
                                showSelectedCount={true}
                                errorText={orderLocationsError}
                                options={getLocations(selectedCompany)}
                                onChange={handleSelectedLocationsChange}
                                styles={{ ...locationStyles, labelStyle: styles.requiredFieldLabel }}
                                dataTest={DATATEST.SITE_HEADER.START_ORDER_MODAL.LOCATIONS_SELECTOR_MULTI}
                            />
                        )}
                        {showLocations && !selectedCompany?.__settings.__allow_bulk_ordering && (
                            <SelectField
                                label={format("Location")}
                                name="location"
                                errorText={orderLocationsError}
                                width={`${FIELD_WIDTH}px`}
                                style={styles.bottomFields}
                                value={selectedLocation}
                                dataTest={DATATEST.SITE_HEADER.START_ORDER_MODAL.LOCATIONS_SELECTOR}
                            >
                                {getLocations(selectedCompany).map(({ value, label }) => (
                                    <MenuItem
                                        key={value}
                                        value={value}
                                        primaryText={label}
                                        style={locationStyles.singleSelect}
                                        onClick={handleSelectLocation(value)}
                                        data-test={DATATEST.SITE_HEADER.START_ORDER_MODAL.LOCATIONS_SELECTOR_OPTION}
                                    />
                                ))}
                            </SelectField>
                        )}
                    </div>
                </div>
            </Form>
        </Dialog>
    );
};
